import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiLock, FiUser, FiKey, FiAlertCircle } from 'react-icons/fi';
import authService from '../../utils/authService';

// Get path to make sure we're on the right login page
const ADMIN_ACCESS_PATH = process.env.REACT_APP_ADMIN_PATH || 'secure-admin-route';

// The access code is now checked server-side only
// No hard-coded fallback here

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(true);
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Get the redirect path from location state, or fallback to dashboard
  const from = location.state?.from?.pathname || `/${ADMIN_ACCESS_PATH}/dashboard`;

  // Check if we're accessing from the correct URL
  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    
    // If not accessing from the secret path, redirect to home
    if (pathSegments[1] !== ADMIN_ACCESS_PATH) {
      navigate('/', { replace: true });
    }
    
    // Check if already logged in
    const checkAuth = async () => {
      try {
        // Validate existing session
        const result = await authService.validateSession();
        if (result.success) {
          navigate(from || `/${ADMIN_ACCESS_PATH}/dashboard`);
        }
      } catch (error) {
        // Not logged in, stay on login page
        console.error('Auth check error:', error);
      }
    };
    
    checkAuth();
  }, [navigate, from]);

  // Handle the first step - access code validation
  const handleAccessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Validate access code against the server instead of checking locally
      const result = await authService.validateAccessCode(accessCode);
      
      if (result.success) {
        setShowAccessForm(false);
        setError('');
      } else {
        setError(result.error || 'Invalid access code');
      }
    } catch (err) {
      console.error('Access code validation error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Basic client-side validation
      if (!email || !validateEmail(email)) {
        setError('Please enter a valid email address');
        setLoading(false);
        return;
      }
      
      if (!password || password.length < 6) {
        setError('Password must be at least 6 characters');
        setLoading(false);
        return;
      }
      
      // Call the login API
      const result = await authService.login({
        email,
        password,
        accessCode
      });
      
      if (result.success) {
        // Redirect to admin dashboard
        navigate(`/${ADMIN_ACCESS_PATH}/dashboard`);
      } else {
        // Show error message
        setError(result.error || 'Authentication failed');
        
        // Show attempts left if provided
        if (result.attemptsLeft !== undefined) {
          setAttemptsLeft(result.attemptsLeft);
        }
        
        // If access code is invalid, go back to access code form
        if (result.error && result.error.includes('access code')) {
          setShowAccessForm(true);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Basic email validation
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  if (showAccessForm) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-dark py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-background p-8 rounded-lg shadow-md border border-gray-800">
          <div>
            <h1 className="text-2xl font-bold text-center text-primary mb-2">GRAILED804</h1>
            <h2 className="text-xl font-semibold text-center text-primary">Admin Access</h2>
          </div>
          
          {error && (
            <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded relative flex items-center">
              <FiAlertCircle className="h-5 w-5 mr-2" />
              {error}
            </div>
          )}
          
          <form className="mt-8 space-y-6" onSubmit={handleAccessSubmit}>
            <div className="rounded-md -space-y-px">
              <div className="mb-4">
                <label htmlFor="accessCode" className="sr-only">Access Code</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiKey className="h-5 w-5 text-gray-500" />
                  </div>
                  <input
                    id="accessCode"
                    name="accessCode"
                    type="password"
                    required
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-700 rounded-md bg-background-light text-primary focus:ring-accent focus:border-accent"
                    placeholder="Enter access code"
                    autoFocus
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent rounded-md text-white bg-accent hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors disabled:opacity-70"
              >
                {loading ? 'Validating...' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-dark py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-background p-8 rounded-lg shadow-md border border-gray-800">
        <div>
          <h1 className="text-2xl font-bold text-center text-primary mb-2">GRAILED804</h1>
          <h2 className="text-xl font-semibold text-center text-primary">Admin Login</h2>
        </div>
        
        {error && (
          <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded relative flex items-center">
            <FiAlertCircle className="h-5 w-5 mr-2" />
            <div>
              {error}
              {attemptsLeft !== null && attemptsLeft > 0 && (
                <div className="text-sm mt-1">
                  You have {attemptsLeft} login {attemptsLeft === 1 ? 'attempt' : 'attempts'} remaining.
                </div>
              )}
            </div>
          </div>
        )}
        
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div className="rounded-md -space-y-px">
            <div className="mb-4">
              <label htmlFor="email" className="sr-only">Email address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiUser className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-700 rounded-md bg-background-light text-primary focus:ring-accent focus:border-accent"
                  placeholder="Email address"
                  autoFocus
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-700 rounded-md bg-background-light text-primary focus:ring-accent focus:border-accent"
                  placeholder="Password"
                />
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent rounded-md text-white bg-accent hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors disabled:opacity-70"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowAccessForm(true)}
              className="text-sm text-accent hover:text-accent/80 transition-colors"
            >
              Back to access code
            </button>
          </div>
        </form>
        
        <div className="text-sm text-center mt-6">
          <p className="text-gray-500">
            Demo credentials: admin@grailed804.com / admin123
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin; 